import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TeamName } from '@business-game/shared';

interface TeamCardProps {
  teamNumber: number;
  onSelect: () => void;
}

const teamConfigs: Array<{ name: TeamName; color: 'indigo' | 'emerald' | 'blue' | 'orange'; icon: React.ReactNode; watermark: React.ReactNode }> = [
  {
    name: 'team1' as TeamName,
    color: 'indigo',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    watermark: (
      <svg fill="currentColor" viewBox="0 0 24 24">
        <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
  {
    name: 'team2' as TeamName,
    color: 'emerald',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    watermark: (
      <svg fill="currentColor" viewBox="0 0 24 24">
        <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  {
    name: 'team3' as TeamName,
    color: 'blue',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
      </svg>
    ),
    watermark: (
      <svg fill="currentColor" viewBox="0 0 24 24">
        <path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
      </svg>
    ),
  },
  {
    name: 'team4' as TeamName,
    color: 'orange',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    watermark: (
      <svg fill="currentColor" viewBox="0 0 24 24">
        <path d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
];

export const TeamCard: React.FC<TeamCardProps> = ({ teamNumber, onSelect }) => {
  const { t } = useTranslation();
  const config = teamConfigs[teamNumber - 1];
  const colorClasses = {
    indigo: {
      iconBg: 'bg-indigo-50',
      iconText: 'text-indigo-600',
      hoverIconBg: 'group-hover:bg-indigo-600',
      hoverIconText: 'group-hover:text-white',
      hoverText: 'group-hover:text-indigo-600',
      bar: 'bg-indigo-200',
      hoverBar: 'group-hover:w-full',
      hoverBarColor: 'group-hover:text-indigo-500',
      watermark: 'text-indigo-100',
    },
    emerald: {
      iconBg: 'bg-emerald-50',
      iconText: 'text-emerald-600',
      hoverIconBg: 'group-hover:bg-emerald-600',
      hoverIconText: 'group-hover:text-white',
      hoverText: 'group-hover:text-emerald-600',
      bar: 'bg-emerald-200',
      hoverBar: 'group-hover:w-full',
      hoverBarColor: 'group-hover:text-emerald-500',
      watermark: 'text-emerald-100',
    },
    blue: {
      iconBg: 'bg-blue-50',
      iconText: 'text-blue-600',
      hoverIconBg: 'group-hover:bg-blue-600',
      hoverIconText: 'group-hover:text-white',
      hoverText: 'group-hover:text-blue-600',
      bar: 'bg-blue-200',
      hoverBar: 'group-hover:w-full',
      hoverBarColor: 'group-hover:text-blue-500',
      watermark: 'text-blue-100',
    },
    orange: {
      iconBg: 'bg-orange-50',
      iconText: 'text-orange-600',
      hoverIconBg: 'group-hover:bg-orange-600',
      hoverIconText: 'group-hover:text-white',
      hoverText: 'group-hover:text-orange-600',
      bar: 'bg-orange-200',
      hoverBar: 'group-hover:w-full',
      hoverBarColor: 'group-hover:text-orange-500',
      watermark: 'text-orange-100',
    },
  } as const;

  const colors = colorClasses[config.color];

  return (
    <div className="slide-up" style={{ animationDelay: `${(teamNumber - 1) * 0.1 + 0.2}s` }}>
      <button
        onClick={onSelect}
        className="w-full h-full min-h-[320px] group relative flex flex-col bg-white rounded-3xl p-8 text-left transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl shadow-xl shadow-slate-200/50 border border-white/60 overflow-hidden ring-1 ring-slate-100"
      >
        {/* Watermark */}
        <div className={`absolute -bottom-10 -right-10 w-48 h-48 ${colors.watermark} transform rotate-12 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6`}>
          {config.watermark}
        </div>

        {/* Card Header */}
        <div className="relative z-10 flex justify-between items-start mb-6">
          <div className={`w-16 h-16 rounded-2xl ${colors.iconBg} ${colors.iconText} flex items-center justify-center shadow-sm ${colors.hoverIconBg} ${colors.hoverIconText} transition-colors duration-300`}>
            {config.icon}
          </div>
          <span className="text-6xl font-black text-slate-100 group-hover:text-slate-200 transition-colors">
            {String(teamNumber).padStart(2, '0')}
          </span>
        </div>

        {/* Card Content */}
        <div className="relative z-10 mt-auto space-y-2">
          <h3 className={`text-3xl font-black text-slate-900 ${colors.hoverText} transition-colors`}>
            {t('selectPlayer.team', { number: teamNumber })}
          </h3>
          <div className={`h-1 w-12 ${colors.bar} rounded-full ${colors.hoverBar} transition-all duration-500`}></div>
          <p className={`text-sm font-semibold text-slate-400 pt-2 ${colors.hoverBarColor} flex items-center gap-2`}>
            <span>{t('selectPlayer.startSession')}</span>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </p>
        </div>
      </button>
    </div>
  );
};
